/**
 * HiuConsentDetailPage — /org/health-info/consents/:consentId (HIU_FLOW_105/106/202/301
 * for the consent side; HIU_FLOW_107-113 for the fetch side, F4/F5).
 *
 * Data visibility is gated on the domain `status` only (never abdmStatus / the chip):
 * records can be requested and viewed only while the consent is active (GRANTED/ACTIVE and
 * not past its erase-at) — the server enforces the same rule (CONSENT_NOT_ACTIVE).
 *
 * Several organisations: a grant across several organisations (HIPs) has one artefact per
 * organisation. Each artefact carries its own fetchStatus / recordsReceived / failureReason
 * (rendered as given by OrgFetchSection). The page polls the CONSENT DETAIL (one GET, single
 * flight, 20s; 60s after 30 min; gives up after the 2 h window) only while the consent itself is
 * REQUESTED (waiting for the patient) or any artefact is REQUESTED/RECEIVING, and stops when neither
 * holds or on unmount. Get records / Try again are consent-wide (R187): one request
 * re-asks every pending or failed organisation. Our own organisation is never fetched (R184).
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

import AdminPageHeader from '../components/common/AdminPageHeader';
import ConsentStatusChip from '../components/hiu/ConsentStatusChip';
import OrgFetchSection, {
  ASKABLE_FETCH_STATUSES, PENDING_FETCH_STATUSES, POLL_INTERVAL_MS, POLL_SLOW_INTERVAL_MS,
  POLL_SLOW_AFTER_MS, POLL_WINDOW_MS,
} from '../components/hiu/OrgFetchSection';
import RequestRecordsForm, { scopeOf } from '../components/hiu/RequestRecordsForm';
import { noteStyle } from '../components/hiu/noteStyle';
import { isServerActive, notSentReason, showsAccessUntil, orgsOf, orgLabel } from '../components/hiu/consentOrgs';
import { statusTimeLine } from '../components/hiu/consentStatusTime';
import { useTheme } from '../theme/ThemeContext';
import { usePermission } from '../context/PermissionContext';
import { getConsent, checkConsentStatus, requestHealthInfo } from '../services/api/hiuApi';
import { formatDateIST, formatDateTimeISTWithYear } from '../utils/formatIST';
import { ROUTES } from '../constants/routes';
import {
  OTHER_ORGS, STATUS_CARDS, STATUS_CARD_NEW_REQUEST, STATUS_CARD_NO_PERMISSION, ACCESS_ENDED,
} from '../constants/abdmCopy';
import { hiTypeLabel } from '../constants/abdmHiTypes';
import { purposeLabel } from '../constants/abdmPurposes';
import AbdmEnabledGate from '../components/common/AbdmEnabledGate';

const dash = (v) => v || '—';

function sameSet(a, b) {
  return a.length === b.length && a.every((x) => b.includes(x));
}

// R4: the patient can edit the request while granting. Only meaningful once the granted
// scope has arrived.
function scopeChanged(c) {
  if (!c.grantedScopeReceived) return false;
  return !sameSet(c.hiTypes, c.requestedHiTypes)
    || c.dateFrom !== c.requestedDateFrom
    || c.dateTo !== c.requestedDateTo
    || (c.requestedExpiresAt && c.expiresAt !== c.requestedExpiresAt);
}

// Plain-words result of "Check now" on a consent still waiting for the patient (R7).
function checkStatusMessage(result) {
  if (result.action === 'NONE') return 'ABDM has no newer status for this request yet.';
  return 'We asked ABDM for the latest status. This page has been refreshed; check again in a minute if it has not changed.';
}

function askErrorText(err) {
  if (err.code === 'OWN_ORGANISATION') return OTHER_ORGS.ownOrgText;
  if (err.code === 'SCOPE_UNKNOWN') return OTHER_ORGS.scopeUnknownHint;
  return err.userMessage || OTHER_ORGS.getFailed;
}

function HiuConsentDetailPageBody() {
  const { consentId } = useParams();
  const { isDark } = useTheme();
  const navigate = useNavigate();

  const canRequest = usePermission('HIU_CONSENT_REQUEST');
  const [consent, setConsent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [checkingNow, setCheckingNow] = useState(false);
  const [askError, setAskError] = useState('');
  const [asking, setAsking] = useState(false);
  const askInFlight = useRef(false);
  const pollInFlight = useRef(false);

  const [checking, setChecking] = useState(false);
  const [checkMessage, setCheckMessage] = useState('');
  const [checkIsError, setCheckIsError] = useState(false);
  const checkInFlight = useRef(false);

  const textMuted = isDark ? '#9ca3af' : '#6b7280';

  // `silent` refreshes in place (no spinner) so the page does not unmount mid-interaction.
  const loadConsent = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    setError('');
    try {
      setConsent(await getConsent(consentId));
    } catch (err) {
      setError(err.userMessage || 'Failed to load consent.');
    } finally {
      setLoading(false);
    }
  }, [consentId]);

  useEffect(() => { loadConsent(); }, [loadConsent]);

  const orgs = useMemo(() => orgsOf(consent), [consent]);
  const active = isServerActive(consent);
  // Primitive (the effect dep): true while the patient has not answered yet (HIU_FLOW_202) or any
  // organisation is still being waited on.
  const polling = consent?.displayStatus === 'REQUESTED'
    || (active && orgs.some((a) => PENDING_FETCH_STATUSES.has(a.fetchStatus)));

  // Poll the consent detail: one in flight at a time. A 20 s tick; after 30 min only every 60 s;
  // gives up after the 2 h push window (the server marks the fetch TIMEOUT then). Stops on unmount.
  useEffect(() => {
    if (!polling) return undefined;
    const start = Date.now();
    let lastPoll = start;
    const id = setInterval(async () => {
      const now = Date.now();
      const age = now - start;
      if (age > POLL_WINDOW_MS) { clearInterval(id); return; }
      if (age >= POLL_SLOW_AFTER_MS && now - lastPoll < POLL_SLOW_INTERVAL_MS - 1000) return;
      if (pollInFlight.current) return;
      pollInFlight.current = true;
      lastPoll = now;
      try { await loadConsent(true); } finally { pollInFlight.current = false; }
    }, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [polling, loadConsent]);

  // "Check now": a user-triggered re-GET of the consent detail.
  const handleCheckNow = async () => {
    if (checkingNow) return;
    setCheckingNow(true);
    try { await loadConsent(true); } finally { setCheckingNow(false); }
  };

  // One request fetches from every pending/failed organisation: reload once for the new states.
  const handleSubmitted = () => { setAskError(''); return loadConsent(true); };

  // Try again: the same consent-wide request over the full allowed scope. The
  // server narrows each organisation and answers 409 OWN_ORGANISATION when nothing is askable.
  const handleAskAgain = async () => {
    if (askInFlight.current) return;
    askInFlight.current = true;
    setAsking(true);
    setAskError('');
    try {
      const scope = scopeOf(consent);
      const to = Math.min(new Date(scope.to).getTime(), Date.now());
      await requestHealthInfo({
        consentArtefactId: consent.consentId,
        dateFrom: new Date(scope.from).toISOString(),
        dateTo: new Date(to).toISOString(),
        hiTypes: scope.hiTypes,
      });
      await loadConsent(true);
    } catch (err) {
      setAskError(askErrorText(err));
    } finally {
      askInFlight.current = false;
      setAsking(false);
    }
  };

  // R7: ask ABDM for the current state. Single-flight; then refresh the consent once.
  const handleCheckStatus = async () => {
    if (checkInFlight.current) return;
    checkInFlight.current = true;
    setChecking(true);
    setCheckMessage('');
    try {
      const result = await checkConsentStatus(consentId);
      setCheckIsError(false);
      setCheckMessage(checkStatusMessage(result));
      await loadConsent(true);
    } catch (err) {
      setCheckIsError(true);
      setCheckMessage(
        err.code === 'ABDM_NOT_ACKNOWLEDGED'
          ? 'ABDM has not acknowledged this request yet. Please try again in a few minutes.'
          : (err.userMessage || 'Could not reach ABDM. Please try again.'),
      );
    } finally {
      checkInFlight.current = false;
      setChecking(false);
    }
  };

  const bg = isDark
    ? 'linear-gradient(135deg, #0E0E0F 0%, #1a1a2e 50%, #0E0E0F 100%)'
    : 'linear-gradient(135deg, #f8fafc 0%, #eef2ff 50%, #f8fafc 100%)';
  const cardStyle = {
    background: isDark ? 'rgba(255,255,255,0.03)' : '#ffffff',
    border: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : '#e5e7eb'}`,
    borderRadius: 12,
    padding: 20,
    marginBottom: 16,
  };
  const errorColor = isDark ? '#f87171' : '#dc2626';
  const linkButton = {
    background: isDark ? '#1e293b' : '#f1f5f9', color: isDark ? '#f1f5f9' : '#374151',
    border: `1px solid ${isDark ? 'rgba(255,255,255,0.15)' : '#d1d5db'}`,
    borderRadius: 6, padding: '6px 14px', fontSize: 13, cursor: 'pointer',
  };

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', background: bg }}>
        <AdminPageHeader />
        <main style={{ maxWidth: 720, margin: '0 auto', padding: '24px 16px', color: textMuted }}>
          Loading…
        </main>
      </div>
    );
  }

  if (error && !consent) {
    return (
      <div style={{ minHeight: '100vh', background: bg }}>
        <AdminPageHeader />
        <main style={{ maxWidth: 720, margin: '0 auto', padding: '24px 16px' }}>
          <p style={{ color: errorColor }}>{error}</p>
          <button onClick={() => navigate(ROUTES.HIU_CONSENTS)} style={linkButton}>
            ← Back to list
          </button>
        </main>
      </div>
    );
  }

  const timeLine = statusTimeLine(consent);
  // The server's display_status only (EXPIRED included): no client-clock override.
  const cardKey = consent.displayStatus;
  const statusCard = STATUS_CARDS[cardKey] || null;
  const accessEnded = cardKey === 'EXPIRED';
  // FT-R6: a request that expired unanswered was never granted, so it has no "Access ended" time:
  // it shows the requested "Access until" instead (FT-R7), and the "Expired at" line is its end time.
  const expiredUnanswered = accessEnded && !consent.grantedAt && !!consent.requestExpiryAt;
  const canCheckStatus = consent.displayStatus === 'REQUESTED'
    || (consent.displayStatus === 'GRANTED' && !consent.grantedScopeReceived);
  const others = orgs.filter((a) => !a.isOwnOrganisation);
  const hasOwn = orgs.some((a) => a.isOwnOrganisation);
  const anyPending = others.some((a) => PENDING_FETCH_STATUSES.has(a.fetchStatus));
  const showForm = active && !anyPending && others.some((a) => ASKABLE_FETCH_STATUSES.has(a.fetchStatus));
  const anyFetchFailed = others.some((a) => a.fetchStatus === 'FAILED');
  const changed = scopeChanged(consent);

  return (
    <div style={{ minHeight: '100vh', background: bg }}>
      <AdminPageHeader />

      <main style={{ maxWidth: 720, margin: '0 auto', padding: '24px 16px' }}>
        <button
          onClick={() => navigate(ROUTES.HIU_CONSENTS)}
          style={{
            background: 'transparent', border: 'none', color: textMuted, fontSize: 13,
            cursor: 'pointer', padding: 0, marginBottom: 12,
          }}
        >
          ← Back to list
        </button>

        <div style={cardStyle}>
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
            <div>
              <h1 style={{ fontSize: 20, fontWeight: 700, color: isDark ? '#f5f5f7' : '#111827', margin: 0 }}>
                {consent.patientName || 'Patient'}
              </h1>
              <p style={{ margin: '4px 0 0', fontSize: 13, color: textMuted }}>{consent.patientAbhaAddress}</p>
              <p style={{ margin: '2px 0 0', fontSize: 13, color: textMuted }}>
                {OTHER_ORGS.abhaNumber}: {consent.patientAbhaNumberMasked || '—'}
              </p>
            </div>
            <ConsentStatusChip displayStatus={cardKey} isDark={isDark} />
          </div>

          <div style={{ marginTop: 16, fontSize: 13, color: textMuted, display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span>Requested at: {dash(formatDateTimeISTWithYear(consent.createdAt))}</span>
            {cardKey === 'REQUESTED' && consent.requestExpiryAt && (
              <span>{OTHER_ORGS.requestExpires}: {formatDateTimeISTWithYear(consent.requestExpiryAt)}</span>
            )}
            {timeLine && <span>{timeLine.label} at: {timeLine.at}</span>}
            {showsAccessUntil(cardKey) && (
              <span>
                {accessEnded && !expiredUnanswered ? ACCESS_ENDED : OTHER_ORGS.accessUntil}: {dash(formatDateTimeISTWithYear(consent.expiresAt))}
              </span>
            )}
          </div>

          {statusCard && (
            <div style={noteStyle(statusCard.kind, isDark)} data-testid="status-card">
              <b>{statusCard.bold}</b>{' '}
              {cardKey === 'SUBMIT_FAILED' ? (notSentReason(consent, 'card') || consent.failureReason || statusCard.reasonFallback) : ''}{' '}
              {statusCard.rest}
              {statusCard.action && (
                <div style={{ marginTop: 10 }}>
                  {canRequest ? (
                    <button
                      type="button" onClick={() => navigate(ROUTES.HIU_CONSENT_REQUEST)}
                      style={{
                        background: isDark ? '#0ea5e9' : '#0369a1', color: isDark ? '#0b1220' : '#ffffff',
                        border: 'none', borderRadius: 6, padding: '6px 14px', fontSize: 13, fontWeight: 600, cursor: 'pointer',
                      }}
                    >
                      {STATUS_CARD_NEW_REQUEST}
                    </button>
                  ) : (
                    <span>{STATUS_CARD_NO_PERMISSION}</span>
                  )}
                </div>
              )}
            </div>
          )}

          {canCheckStatus && canRequest && (
            <div style={{ marginTop: 12 }}>
              <button
                onClick={handleCheckStatus} disabled={checking}
                style={{ ...linkButton, opacity: checking ? 0.6 : 1, cursor: checking ? 'not-allowed' : 'pointer' }}
              >
                {checking ? OTHER_ORGS.checking : OTHER_ORGS.checkNow}
              </button>
              {checkMessage && (
                <p role="status" style={{ fontSize: 13, margin: '8px 0 0', color: checkIsError ? errorColor : textMuted }}>
                  {checkMessage}
                </p>
              )}
            </div>
          )}

          {error && (
            <p style={{ color: errorColor, fontSize: 13, marginTop: 12 }}>{error}</p>
          )}
        </div>

        {/* FT-R7: shown for every status (a waiting or refused request included). */}
        <div style={cardStyle}>
          <h2 style={{ fontSize: 15, fontWeight: 700, color: isDark ? '#f5f5f7' : '#111827', margin: '0 0 8px' }}>
            {consent.grantedScopeReceived ? 'What the patient allowed' : 'What was requested'}
          </h2>
          <div style={{ fontSize: 13, color: textMuted, display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span>{OTHER_ORGS.scopeTypes}: {consent.hiTypes.map(hiTypeLabel).join(', ') || '—'}</span>
            <span>
              Date range: {dash(formatDateIST(consent.dateFrom))} to {dash(formatDateIST(consent.dateTo))}
            </span>
            <span>{OTHER_ORGS.purpose}: {dash(purposeLabel(consent.purposeCode))}</span>
          </div>
          {changed && (
            <p style={{ fontSize: 13, margin: '8px 0 0', color: isDark ? '#fbbf24' : '#b45309' }}>
              The patient changed this from what we requested: {consent.requestedHiTypes.map(hiTypeLabel).join(', ') || '—'};{' '}
              {dash(formatDateIST(consent.requestedDateFrom))} to {dash(formatDateIST(consent.requestedDateTo))}.
            </p>
          )}
        </div>

        {showForm && !canRequest && (
          <p style={{ fontSize: 13, color: textMuted }}>
            You don't have permission to request records. Ask your admin.
          </p>
        )}

        {/* R198: a FAILED section has its own Try again, so the form (and its retry button) is hidden then. */}
        {showForm && canRequest && !anyFetchFailed && (
          <div style={cardStyle}>
            <h2 style={{ fontSize: 15, fontWeight: 700, color: isDark ? '#f5f5f7' : '#111827', margin: '0 0 12px' }}>
              {OTHER_ORGS.getRecords}
            </h2>
            <RequestRecordsForm
              consent={consent} onSubmitted={handleSubmitted} isDark={isDark}
            />
            {hasOwn && (
              <p style={{ margin: '8px 0 0', fontSize: 12, color: textMuted }}>{OTHER_ORGS.ownNotIncluded}</p>
            )}
          </div>
        )}

        {askError && (
          <p role="alert" style={{ color: errorColor, fontSize: 13, marginBottom: 12 }}>{askError}</p>
        )}

        {active && orgs.map((a) => (
          <OrgFetchSection
            key={a.artefactId}
            artefact={a}
            label={orgLabel(a)}
            active={active}
            canRequest={canRequest}
            onAsk={handleAskAgain}
            onNewRequest={() => navigate(ROUTES.HIU_CONSENT_REQUEST)}
            onCheck={handleCheckNow}
            onView={() => navigate(ROUTES.HIU_CONSENT_RECORDS.replace(':consentId', consentId))}
            busy={asking || checkingNow}
            isDark={isDark}
            cardStyle={cardStyle}
          />
        ))}
      </main>
    </div>
  );
}

export default function HiuConsentDetailPage() {
  return (
    <AbdmEnabledGate>
      <HiuConsentDetailPageBody />
    </AbdmEnabledGate>
  );
}
